import { isAnyOf } from "@reduxjs/toolkit";

import { keysToCamelCase } from "@jti/fe-common";
import type { MagentoOrderPrices, OrderPrices } from "@jti/magento";

import { startListening } from "../middlewares/listenerMiddleware";
import {
  clearFreeGiftsErrors,
  fetchCartPrices,
  fetchStoreConfig,
  removeItem,
  removeItems,
  updateItemQuantity,
} from "../slices/cartSlice";
import { fetchCart } from "../slices/cartSlice/actions/fetchCart";
import {
  fetchCartItems,
  fetchCleanCartItems,
  removeInvalidFreeGifts,
} from "../slices/cartSlice/actions/fetchCartItems";
import { magentoApiSlice } from "../slices/magentoApiSlice";
import { TAGS } from "../slices/magentoApiSlice/common/tags";
import { normalizeStoreConfigResponse } from "../slices/magentoApiSlice/utils/normalizeStoreConfigResponse";

const isOrderPriceCurrency = (currency: unknown): currency is OrderPrices["grandTotal"]["currency"] =>
  typeof currency === "string";

startListening({
  matcher: isAnyOf(updateItemQuantity.fulfilled, removeItem.fulfilled, removeItems.fulfilled),
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(
      magentoApiSlice.util.invalidateTags([TAGS.CART_ITEMS, TAGS.CART_ORDER_PRICES, TAGS.SHIPPING_METHODS])
    );
  },
});

startListening({
  actionCreator: fetchCleanCartItems.fulfilled,
  effect: (action, listenerApi) => {
    const cartId = action.meta.arg;

    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.CART_ORDER_PRICES]));
    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        { endpointName: "getCartItems", arg: { cartId }, value: action.payload },
      ])
    );
  },
});

startListening({
  actionCreator: removeInvalidFreeGifts.fulfilled,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(magentoApiSlice.util.invalidateTags([TAGS.CART_ITEMS, TAGS.CART_ORDER_PRICES]));
  },
});

startListening({
  actionCreator: fetchCart.fulfilled,
  effect: (action, listenerApi) => {
    if (!action.payload.id) {
      return;
    }

    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryData("getCustomerCartId", undefined, {
        __typename: "Query",
        customerCart: {
          __typename: "Cart",
          id: action.payload.id,
        },
      })
    );
  },
});

startListening({
  actionCreator: fetchStoreConfig.fulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        { endpointName: "getStoreConfig", arg: undefined, value: normalizeStoreConfigResponse(action.payload) },
      ])
    );
  },
});

/**
 * When setShippingAddressOnCartWithMethods succeeds, write the returned shipping
 * methods directly into the getShippingMethods query cache (avoids a refetch).
 */
startListening({
  matcher: magentoApiSlice.endpoints.setShippingAddressOnCartWithMethods.matchFulfilled,
  effect: (action, listenerApi) => {
    const { cartId } = action.meta.arg.originalArgs;
    listenerApi.dispatch(magentoApiSlice.util.upsertQueryData("getShippingMethods", cartId, action.payload));
  },
});

/*
 * @description: This listener updates the cache for the "getCustomerOrder" query when a new order is placed.
 */
startListening({
  matcher: magentoApiSlice.endpoints.placeOrder.matchFulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        {
          endpointName: "getCustomerOrder",
          arg: action.payload.number,
          value: action.payload,
        },
      ])
    );
  },
});

/**
 * When getCartItems returns gift items with errors (e.g. promo code was removed),
 * remove them from the backend and record their errors. We reuse the items already returned by
 * getCartItems and let removeInvalidFreeGifts.fulfilled invalidate the cache.
 */
startListening({
  matcher: magentoApiSlice.endpoints.getCartItems.matchFulfilled,
  effect: (action, listenerApi) => {
    const { items } = action.payload;
    const hasInvalidGifts = items.some((item) => item.isGift && item.id && item.errors?.length);

    if (!hasInvalidGifts) {
      listenerApi.dispatch(clearFreeGiftsErrors());
      return;
    }

    const { cartId } = action.meta.arg.originalArgs;
    listenerApi.dispatch(removeInvalidFreeGifts({ cartId, items }));
  },
});

// The thunk payload is already in GetCartItemsResult shape, so it must bypass the endpoint transformResponse.
startListening({
  actionCreator: fetchCartItems.fulfilled,
  effect: (action, listenerApi) => {
    const cartId = typeof action.meta.arg === "string" ? action.meta.arg : action.meta.arg.cartId;

    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        { endpointName: "getCartItems", arg: { cartId }, value: action.payload },
      ])
    );
  },
});

startListening({
  actionCreator: fetchCartPrices.fulfilled,
  effect: (action, listenerApi) => {
    const cartId = action.meta.arg;
    const camelCasedPrices = keysToCamelCase(action.payload.prices);
    const minimumPayableAmount = action.payload.minimum_payable_amount;

    const defaultedPrices = {
      ...camelCasedPrices,
      appliedCampaign: camelCasedPrices.appliedCampaign ?? {
        loyaltyDiscountAmount: 0,
        loyaltyDiscountUuid: "",
      },
    };

    const seededPrices: MagentoOrderPrices = minimumPayableAmount
      ? {
          ...defaultedPrices,
          ...(isOrderPriceCurrency(minimumPayableAmount.currency)
            ? {
                minimumPayableAmount: {
                  value: minimumPayableAmount.value,
                  currency: minimumPayableAmount.currency,
                },
              }
            : {}),
        }
      : defaultedPrices;

    listenerApi.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        {
          endpointName: "getCustomerCartOrderPrices",
          arg: cartId,
          value: seededPrices,
        },
      ])
    );
  },
});
